import SearchIcon from "../../shared/Icons/SearchIcon";
import { ReactComponent as PlusSign } from "../../shared/assets/plus.svg";
import { themeAtom } from "../../shared/atoms";
import { DriverProps } from "../models/AddKeyDriverButton.model";
import { useAtomValue } from "jotai";
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { twMerge } from "tailwind-merge";

const AddKeyDriverButton: React.FC<DriverProps> = ({ drivers }) => {
  const theme = useAtomValue(themeAtom);

  const [showOverlay, setShowOverlay] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredDriver, setHoveredDriver] = useState<{
    name: string;
    definition: string;
    top: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const normalizedQuery = searchTerm.trim().toLowerCase();

  const filteredDrivers = useMemo(() => {
    if (!normalizedQuery) return drivers;
    return drivers.filter((driver) => driver.name.toLowerCase().includes(normalizedQuery));
  }, [drivers, normalizedQuery]);

  const toggleOverlay = useCallback(() => {
    setShowOverlay((prev) => !prev);
  }, []);

  const closeOverlay = useCallback(() => {
    setShowOverlay(false);
    setHoveredDriver(null);
    // keep search value as-is to preserve the user’s filter when re-opening
  }, []);

  const showDriverDefinition = useCallback(
    (driver: DriverProps["drivers"][number], element: HTMLLIElement) => {
      if (!driver.definition) {
        setHoveredDriver(null);
        return;
      }

      setHoveredDriver({
        name: driver.name,
        definition: driver.definition,
        top: (listRef.current?.offsetTop ?? 0) + element.offsetTop,
      });
    },
    [],
  );

  const clearDriverDefinition = useCallback(() => {
    setHoveredDriver(null);
  }, []);

  // Close when clicking outside the button+menu container (no visual changes)
  useEffect(() => {
    if (!showOverlay) return;

    const handleClickOutside = (event: MouseEvent) => {
      const root = containerRef.current;
      if (root && !root.contains(event.target as Node)) {
        closeOverlay();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOverlay, closeOverlay]);

  useEffect(() => {
    if (!hoveredDriver) return;
    if (filteredDrivers.some((driver) => driver.name === hoveredDriver.name)) return;
    setHoveredDriver(null);
  }, [filteredDrivers, hoveredDriver]);

  // Close on Escape (no layout changes)
  useEffect(() => {
    if (!showOverlay) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeOverlay();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showOverlay, closeOverlay]);

  return (
    <div
      ref={containerRef}
      className={twMerge(
        "absolute -top-[0.5px] right-0 z-20 flex h-[44px] rounded-tr-xl px-4",
        theme.home.tailwind.panelBackground,
      )}
    >
      <div className="flex items-center justify-center">
        <button
          type="button"
          onClick={toggleOverlay}
          className="flex items-center gap-2 text-[#1F8475]"
          aria-haspopup="listbox"
          aria-expanded={showOverlay}
        >
          <PlusSign className="h-4 w-4" /> Add key driver
        </button>

        {showOverlay && (
          <div className="absolute right-[100%] top-0.5 z-50 transform">
            <div className="relative flex items-start gap-3">
              <div
                className={twMerge(
                  "flex w-[250px] flex-col items-start rounded-xl border",
                  theme.uwhcAssistant.theadBg,
                  theme.uwhcAssistant.border,
                )}
                style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.25)" }}
              >
                <div className="relative mb-2 mt-3 flex w-full px-3">
                  <input
                    type="text"
                    placeholder="Search a key driver"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={twMerge(
                      "w-full rounded-l-md border px-2.5 text-[14px] focus:outline-none",
                      theme.vesselDetails.fields,
                      theme.uwhcAssistant.inputBorder,
                      theme.uwhcAssistant.aiReplyBg,
                    )}
                  />
                  {/* Keep the visual “search button” exactly as before */}
                  <button
                    type="button"
                    className="flex h-9 items-center justify-center rounded-r-md bg-[#1F8475] px-2"
                    tabIndex={-1} // decorative; no behavior change
                  >
                    <SearchIcon className={theme.warningFunction.text} />
                  </button>
                </div>

                <ul
                  ref={listRef}
                  onMouseLeave={clearDriverDefinition}
                  className="mx-3.5 mb-2 flex w-[210px] flex-col gap-1 text-left font-normal"
                >
                  {filteredDrivers.length > 0 ? (
                    filteredDrivers.map((item) => {
                      const isSelected = item.isOn;
                      const isHovered = hoveredDriver?.name === item.name;
                      return (
                        <li
                          key={item.name}
                          onClick={item.toggle}
                          onMouseEnter={(e) => showDriverDefinition(item, e.currentTarget)}
                          onFocus={(e) => showDriverDefinition(item, e.currentTarget)}
                          onBlur={clearDriverDefinition}
                          tabIndex={0}
                          className={twMerge(
                            "min-h-8 cursor-pointer rounded-md px-2 py-1 text-[14px] leading-5 transition-colors",
                            isSelected
                              ? theme.vesselDetails.neutralText
                              : twMerge("hover:text-[#1F8475]", theme.vesselDetails.textBtn),
                            isHovered ? "bg-[#F1F7F6] text-[#1F8475]" : "",
                          )}
                        >
                          {item.name}
                        </li>
                      );
                    })
                  ) : (
                    <li className="px-2 text-sm italic text-gray-400">No matches found</li>
                  )}
                </ul>
              </div>

              {hoveredDriver && (
                <div
                  className="pointer-events-none absolute right-full mr-3 w-[360px] rounded-xl border border-[#D6DEE8] bg-white p-3 text-left shadow-[0_12px_30px_rgba(15,23,42,0.12)]"
                  style={{ top: hoveredDriver.top }}
                >
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#1F8475]">
                    {hoveredDriver.name}
                  </p>
                  <p className="text-[13px] leading-5 text-[#475569]">{hoveredDriver.definition}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddKeyDriverButton;
